import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, MapPin, Clock, Utensils, Award, CalendarDays, FileText, Download, ZoomIn } from "lucide-react";
import { api, fileUrl } from "@/api";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";
import { SEED_EVENT, getSeedEventStatus } from "@/data/seedEvent";

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function todayDayName() {
  return DAYS[new Date().getDay()];
}

function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="full-menu-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
        aria-label="Close"
        data-testid="full-menu-lightbox-close"
      >
        <X size={20} />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-[92vh] max-w-[95vw] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="full-menu-lightbox-image"
      />
    </div>
  );
}

function FullMenuSection({ vendor }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!vendor?.full_menu_url) return null;

  const isPdf = vendor.full_menu_content_type === "application/pdf";
  const absoluteUrl = fileUrl(vendor.full_menu_url);
  const downloadUrl = absoluteUrl;
  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
    absoluteUrl
  )}&embedded=true`;

  return (
    <div
      className="mt-5 border-t border-slate-100 pt-4"
      data-testid="vendor-panel-full-menu"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
          {isPdf ? <FileText size={12} /> : <Utensils size={12} />} Full menu
        </div>
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-[#E8650A] hover:underline"
          data-testid="full-menu-download"
        >
          <Download size={12} /> Download
        </a>
      </div>

      {isPdf ? (
        <div
          className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
          data-testid="full-menu-pdf-frame-wrap"
        >
          <iframe
            src={googleViewerUrl}
            title={vendor.full_menu_filename || "Full menu PDF"}
            className="w-full h-[480px] bg-white"
            data-testid="full-menu-pdf-frame"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="relative block w-full group rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
          data-testid="full-menu-image-button"
        >
          <img
            src={absoluteUrl}
            alt={vendor.full_menu_filename || "Full menu"}
            className="w-full h-auto object-contain max-h-[520px]"
            data-testid="full-menu-image"
          />
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-black/60 text-white text-[11px] font-medium px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn size={12} /> Tap to zoom
          </span>
        </button>
      )}

      {lightboxOpen && !isPdf && (
        <ImageLightbox
          src={absoluteUrl}
          alt={vendor.full_menu_filename || "Full menu"}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

function VendorLogo({ url, name, size = "lg" }) {
  const dims = size === "lg" ? "h-16 w-16 text-xl" : "h-10 w-10 text-sm";
  if (url) {
    return (
      <img
        src={url}
        alt={name || "Vendor"}
        className={`${dims} rounded-lg object-cover border border-slate-200 bg-slate-50`}
        data-testid="vendor-panel-logo"
      />
    );
  }
  const initials = (name || "FF")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className={`${dims} rounded-lg bg-[#FDECE2] text-[#E8650A] grid place-items-center font-bold border border-[#E8650A]/20`}
      data-testid="vendor-panel-logo-fallback"
    >
      {initials}
    </div>
  );
}

function VendorPanel({ vendorId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setShowMenu(false);
    (async () => {
      try {
        const { data } = await api.get(`/public/vendors/${vendorId}`);
        if (alive) setData(data);
      } catch (err) {
        console.error("[PublicMap] vendor detail load failed:", err?.message || err);
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [vendorId]);

  const today = todayDayName();
  const todaysHours = useMemo(() => {
    if (!data?.hours) return null;
    return data.hours.find((h) => h.day_of_week === today) || null;
  }, [data, today]);

  const hoursLabel = !todaysHours
    ? "Hours not set"
    : todaysHours.is_closed
    ? "Closed today"
    : `${todaysHours.open_time || "—"} → ${todaysHours.close_time || "—"}`;

  const vendor = data?.vendor;
  const isLive = !!vendor?.is_live;
  const cuisines = vendor?.cuisine_tags || [];

  return (
    <aside
      className="bg-white border border-slate-200 rounded-xl shadow-sm w-full lg:w-[360px] flex-shrink-0 overflow-hidden flex flex-col"
      data-testid="vendor-side-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Vendor details
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close panel"
          data-testid="vendor-panel-close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-5 py-5 overflow-y-auto">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="vendor-panel-loading">
            Loading vendor…
          </div>
        ) : !vendor ? (
          <div className="text-sm text-slate-500">Vendor not found.</div>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <VendorLogo url={vendor.logo_url} name={vendor.business_name} />
              <div className="min-w-0">
                <h2
                  className="text-lg font-bold tracking-tight truncate"
                  data-testid="vendor-panel-name"
                >
                  {vendor.business_name || "Vendor"}
                </h2>
                <div className="mt-1">
                  {isLive ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"
                      data-testid="vendor-panel-status-live"
                    >
                      <span className="ff-live-dot-sm" /> Live now
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs text-slate-500"
                      data-testid="vendor-panel-status-offline"
                    >
                      <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                      Offline
                    </span>
                  )}
                </div>
                {cuisines.length > 0 && (
                  <div
                    className="mt-2 flex flex-wrap gap-1.5"
                    data-testid="vendor-panel-cuisines"
                  >
                    {cuisines.slice(0, 4).map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center text-[11px] font-medium bg-[#FDECE2] text-[#E8650A] px-2 py-0.5 rounded-full"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {vendor.description && (
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                {vendor.description}
              </p>
            )}

            <div
              className="mt-5 flex items-center gap-2 text-sm text-slate-700"
              data-testid="vendor-panel-today-hours"
            >
              <Clock size={16} className="text-[#E8650A] flex-shrink-0" />
              <span className="font-medium">{today}:</span>
              <span className="text-slate-600">{hoursLabel}</span>
            </div>

            {data?.current_location && isLive && (
              <div
                className="mt-2 flex items-start gap-2 text-sm text-slate-700"
                data-testid="vendor-panel-current-location"
              >
                <MapPin size={16} className="text-[#E8650A] mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  {data.current_location.name && (
                    <div className="font-medium truncate">
                      {data.current_location.name}
                    </div>
                  )}
                  {data.current_location.address && (
                    <div className="text-slate-500 text-xs">
                      {data.current_location.address}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-5 flex gap-2">
              <Button
                className="ff-btn-primary flex-1"
                onClick={() => setShowMenu((v) => !v)}
                data-testid="vendor-panel-view-menu"
              >
                <Utensils size={14} className="mr-1.5" />
                {showMenu ? "Hide full menu" : "View full menu"}
              </Button>
            </div>

            {/* Menu preview — up to 4 items, always visible */}
            {data?.menu?.length > 0 && !showMenu && (
              <div
                className="mt-5 border-t border-slate-100 pt-4"
                data-testid="vendor-panel-menu-preview"
              >
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Utensils size={12} /> Menu preview
                </div>
                <ul className="space-y-2.5">
                  {data.menu.slice(0, 4).map((m) => (
                    <li
                      key={m.id}
                      className="flex items-start justify-between gap-3"
                      data-testid={`vendor-panel-menu-preview-item-${m.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-slate-800 truncate">
                          {m.name}
                        </div>
                        {m.description && (
                          <div className="text-xs text-slate-500 line-clamp-1">
                            {m.description}
                          </div>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-[#E8650A] flex-shrink-0">
                        ${Number(m.price || 0).toFixed(2)}
                      </div>
                    </li>
                  ))}
                </ul>
                {data.menu.length > 4 && (
                  <div className="text-xs text-slate-400 mt-2">
                    +{data.menu.length - 4} more · tap "View full menu"
                  </div>
                )}
              </div>
            )}

            {showMenu && (
              <div
                className="mt-5 border-t border-slate-100 pt-4"
                data-testid="vendor-panel-menu"
              >
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Menu
                </div>
                {(!data?.menu || data.menu.length === 0) ? (
                  <div
                    className="text-sm text-slate-500"
                    data-testid="vendor-panel-menu-empty"
                  >
                    This vendor hasn't published a menu yet.
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {data.menu.map((m) => (
                      <li
                        key={m.id}
                        className="flex items-start justify-between gap-3"
                        data-testid={`vendor-panel-menu-item-${m.id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-slate-800 truncate">
                            {m.name}
                          </div>
                          {m.description && (
                            <div className="text-xs text-slate-500 line-clamp-2">
                              {m.description}
                            </div>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-[#E8650A] flex-shrink-0">
                          ${Number(m.price || 0).toFixed(2)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <FullMenuSection vendor={vendor} />

            {data?.awards?.length > 0 && (
              <div
                className="mt-5 border-t border-slate-100 pt-4"
                data-testid="vendor-panel-awards"
              >
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Award size={12} /> Awards
                </div>
                <ul className="space-y-1.5">
                  {data.awards.slice(0, 4).map((a) => (
                    <li key={a.id} className="text-sm text-slate-700">
                      {a.title}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data?.events?.length > 0 && (
              <div
                className="mt-5 border-t border-slate-100 pt-4"
                data-testid="vendor-panel-events"
              >
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <CalendarDays size={12} /> Upcoming events
                </div>
                <ul className="space-y-1.5">
                  {data.events.slice(0, 3).map((e) => (
                    <li key={e.id} className="text-sm text-slate-700">
                      <span className="font-medium">{e.name}</span>
                      {e.event_date && (
                        <span className="text-slate-500 ml-1">
                          · {new Date(e.event_date).toLocaleDateString()}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

function EventPanel({ status, onClose }) {
  const statusLabel = status === "active" ? "Happening now" : "Upcoming event";
  const statusClass = status === "active" ? "text-emerald-700" : "text-slate-500";
  const statusDot =
    status === "active" ? (
      <span className="ff-live-dot-sm" />
    ) : (
      <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
    );

  return (
    <aside
      className="bg-white border border-slate-200 rounded-xl shadow-sm w-full lg:w-[360px] flex-shrink-0 overflow-hidden flex flex-col"
      data-testid="event-side-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Event details
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close panel"
          data-testid="event-panel-close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-5 py-5 overflow-y-auto">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-lg bg-[#FDECE2] text-[#E8650A] grid place-items-center border border-[#E8650A]/20 flex-shrink-0">
            <CalendarDays size={28} />
          </div>
          <div className="min-w-0">
            <h2
              className="text-lg font-bold tracking-tight leading-snug"
              data-testid="event-panel-name"
            >
              {SEED_EVENT.name}
            </h2>
            <div
              className={`mt-1 inline-flex items-center gap-1.5 text-xs font-medium ${statusClass}`}
              data-testid="event-panel-status"
            >
              {statusDot} {statusLabel}
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-sm text-slate-700" data-testid="event-panel-when">
          <Clock size={16} className="text-[#E8650A] mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">{SEED_EVENT.date_label}</div>
            <div className="text-slate-600">{SEED_EVENT.time_range_label}</div>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 text-sm text-slate-700" data-testid="event-panel-address">
          <MapPin size={16} className="text-[#E8650A] mt-0.5 flex-shrink-0" />
          <div className="min-w-0 text-slate-700">{SEED_EVENT.address}</div>
        </div>
      </div>
    </aside>
  );
}


export default function PublicMap() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [eventStatus, setEventStatus] = useState(() => getSeedEventStatus());

  // Keep the seed event status fresh — re-evaluate every minute so the pin
  // transitions grey → green → removed without a page reload.
  useEffect(() => {
    const t = setInterval(() => setEventStatus(getSeedEventStatus()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/vendors/live");
        setVendors(data || []);
      } catch (err) {
        console.error("Failed to load live vendors", err);
      }
      setLoading(false);
    })();
  }, []);

  const liveCount = vendors.filter((v) => v.is_live).length;

  const seedMarker = useMemo(() => {
    if (eventStatus === "ended") return null;
    return {
      vendor_id: SEED_EVENT.id,
      business_name: SEED_EVENT.name,
      lat: SEED_EVENT.lat,
      lng: SEED_EVENT.lng,
      address: SEED_EVENT.address,
      is_live: eventStatus === "active",
      is_platform_event: true,
    };
  }, [eventStatus]);

  const allMarkers = useMemo(
    () => (seedMarker ? [...vendors, seedMarker] : vendors),
    [vendors, seedMarker]
  );

  // If the event ended while a user had it selected, clear the selection.
  useEffect(() => {
    if (selectedId === SEED_EVENT.id && !seedMarker) setSelectedId(null);
  }, [seedMarker, selectedId]);

  const isEventSelected = selectedId === SEED_EVENT.id;

  const selectedVendor = useMemo(
    () =>
      isEventSelected
        ? null
        : allMarkers.find((v) => v.vendor_id === selectedId) || null,
    [allMarkers, selectedId, isEventSelected]
  );

  const center = useMemo(() => {
    if (isEventSelected && seedMarker) return [seedMarker.lat, seedMarker.lng];
    if (selectedVendor) return [selectedVendor.lat, selectedVendor.lng];
    if (allMarkers.length) return [allMarkers[0].lat, allMarkers[0].lng];
    return [40.7128, -74.006];
  }, [selectedVendor, allMarkers, isEventSelected, seedMarker]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="sm" withTagline testid="public-map-brand" />
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="outline" size="sm" data-testid="public-map-login">
                Vendor login
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="ff-btn-primary" data-testid="public-map-register">
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 w-full">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Live food trucks</h1>
            <p className="text-slate-500 mt-1">
              <span className="inline-flex items-center gap-1 mr-3">
                <span className="ff-live-dot" /> {liveCount} live now
              </span>
              <span className="inline-flex items-center gap-1 text-slate-500">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-400" />{" "}
                {vendors.length - liveCount} offline
              </span>
            </p>
          </div>
        </div>

        <div
          className="mt-6 flex flex-col lg:flex-row gap-4"
          data-testid="public-map-container"
        >
          <div className="flex-1 min-w-0">
            <MapView
              center={center}
              zoom={allMarkers.length ? 12 : 4}
              markers={allMarkers}
              onMarkerClick={(m) => setSelectedId(m.vendor_id)}
              selectedId={selectedId}
              height={560}
              testid="public-map"
            />
          </div>

          {selectedId && (
            isEventSelected ? (
              <EventPanel
                status={eventStatus}
                onClose={() => setSelectedId(null)}
              />
            ) : (
              <VendorPanel
                vendorId={selectedId}
                onClose={() => setSelectedId(null)}
              />
            )
          )}
        </div>

        <div className="mt-6 text-sm text-slate-500">
          {loading
            ? "Loading map…"
            : allMarkers.length === 0
            ? "No vendors have gone live yet. Be the first!"
            : !selectedId
            ? "Tip: click a pin to see vendor details."
            : null}
        </div>
      </div>
    </div>
  );
}
